import { useEffect, type FC } from 'react';
import { Navigate, useParams } from 'react-router';
import type { Shippers } from 'src/apis/capacity-management/types';
import { useCapacityManagementRoot } from 'src/apis/capacity-management/use-capacity-management-root';
import { ThrowNotFound } from 'src/components/feedback/not-found';
import { PageSpinner } from 'src/components/spinner-container';
import { useAxios } from 'src/hooks/use-axios';
import { stringifySearchParams } from 'src/hooks/use-page-params-from-schema';
import type { ShipperTransactionsPageParams } from 'src/pages/reporting/shipper-transactions/use-page-params';
import { Forbidden } from 'src/utils/errors';

// When the TSO receives an email confirming the capacity allocation, this component redirects to the shipper transactions page with the filter values correctly applied. Used in Route.tsx
export const RedirectAuctionByShipper: FC = () => {
  const { shipperId, assignedAuctionId } = useParams<{
    shipperId: string;
    assignedAuctionId: string;
  }>();

  const capacityManagementRoot = useCapacityManagementRoot();

  const shippers = useAxios(
    (axios, baseConfig, url: string) =>
      axios.request<Shippers>({
        ...baseConfig,
        url,
      }),
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    if (!capacityManagementRoot.response) return;
    const shipperFilterUrl =
      capacityManagementRoot.response.data._links
        ?.myTsoTransactionsShipperFilter?.href;
    if (!shipperFilterUrl) throw new Forbidden();
    shippers.execute(shipperFilterUrl);
    // oxlint-disable-next-line exhaustive-deps
  }, [capacityManagementRoot.response]);

  if (!shippers.response) return <PageSpinner />;

  const shipper = shippers.response.data._embedded.items.find(
    (shipper) => shipper.id === shipperId
  );

  if (!shipper) return <ThrowNotFound />;

  return (
    <Navigate
      replace
      to={{
        pathname: '/reporting/shipper-transactions',
        search: stringifySearchParams({
          shipperId: [
            { value: shipperId, label: shipper.companyName, eic: shipper.eic },
          ],
          transactionTypeSpecificId: assignedAuctionId,
        } satisfies Partial<ShipperTransactionsPageParams['filterValues']>),
      }}
    />
  );
};
